import type { NavigationLink } from '../../common'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { type ShellUserInfo } from '../../core/models'
import { getExtensionsManager } from '../../extensions/extensions-manager'
import { type UserProfileEntitlement } from '../../services/identity/models'
import { getEntitlementsFromUserProfile } from '../../services/identity/userProfile'
import { type InternalNavigationLink } from './types'

export const sortNavigationLinks = (items: NavigationLink[]): NavigationLink[] => {
  const navigationOrder = getExtensionsManager().getNavigationOrder()
  if (!navigationOrder) {
    return items
  }
  const unknownItemIndex = navigationOrder.length
  const compareItems = (item1: NavigationLink, item2: NavigationLink): number => {
    let index1 = navigationOrder.indexOf(item1.id)
    index1 = index1 === -1 ? unknownItemIndex : index1
    let index2 = navigationOrder.indexOf(item2.id)
    index2 = index2 === -1 ? unknownItemIndex : index2
    return index1 - index2
  }
  return items.sort(compareItems)
}

export const getVisibleLinks = async (
  links: InternalNavigationLink[],
  user: ShellUserInfo,
): Promise<InternalNavigationLink[]> =>
  (await resolveLinksVisibility(links, user)).filter((link: InternalNavigationLink) => link._isVisible)

/**
 * Resolve links visibility based on profile info
 */
const resolveLinksVisibility = async (
  links: InternalNavigationLink[],
  user: ShellUserInfo,
): Promise<InternalNavigationLink[]> => {
  const linkHasUnresolvedVisibility = (link: InternalNavigationLink) => link._isVisible === undefined
  const unresolvedLinks = links
    .filter(linkHasUnresolvedVisibility)
    .map((link: InternalNavigationLink) =>
      link.isVisible?.(
        getEntitlementsFromUserProfile(getShellApiInstance().user) as UserProfileEntitlement[],
        user.emails,
      ),
    )

  for (const isVisible of await Promise.all(unresolvedLinks)) {
    const index = links.findIndex(linkHasUnresolvedVisibility)
    if (index >= 0) {
      links[index] = Object.assign({}, { ...links[index], _isVisible: isVisible }) as InternalNavigationLink
    }
  }

  return links
}

export const updateLinksAfterBadgeEvent = (value: number, id: string | undefined, links: InternalNavigationLink[]) =>
  links.map(link => (id === link.id ? { ...link, badgeCount: value } : link))
